<script setup lang="ts">
import { computed, ref, toRaw, watch } from 'vue';
import draggable from 'vuedraggable';
import type { FieldSchema } from '../../../types';

// A list of items, each with its own small form: gallery photos, questions, logos, numbers. The caller passes
// `field.settings.buildItemSchema(item) => FieldSchema[]` and `field.settings.createItem() => item`, and may pass
// `field.settings.getItemFieldError(itemField) => i18n key | null` to show a format error on an item field, so this
// field knows no domain. Every change emits a NEW array: a parent that watches field models by reference would
// never see an item edited in place.

type ListItem = Record<string, unknown>;

interface ListRow {
  schema: FieldSchema[];
}

const props = defineProps<{
  disabled?: boolean;
  error?: string | boolean;
  field?: FieldSchema;
}>();

const model = defineModel<ListItem[]>();

// Drag keys go by row identity: array indexes go stale on every drag, and keys built from the values would
// remount an item's form on each keystroke and drop the cursor.
const rowKeys = new WeakMap<ListRow, number>();
let lastEmittedItems: ListItem[] | undefined;
let nextRowKey = 0;

const rows = ref<ListRow[]>([]);

const isDisabled = computed(() => !!props.field?.state?.disabled || !!props.disabled);
const itemLabel = computed(() => String(props.field?.settings?.itemLabel ?? ''));
const maxItems = computed(() => Number(props.field?.settings?.maxItems ?? 0));

const canAddItem = computed(() => {
  const hasRoom = !maxItems.value || rows.value.length < maxItems.value;

  return !isDisabled.value && hasRoom;
});

watch(() => model.value, onModelChange, { immediate: true });
watch(rows, onRowsChange, { deep: true });

// HELPERS
function getRowKey(row: ListRow): number {
  if (!rowKeys.has(row)) rowKeys.set(row, ++nextRowKey);

  return rowKeys.get(row) as number;
}

function buildRow(item: ListItem): ListRow {
  return { schema: props.field?.settings?.buildItemSchema?.(item) ?? [] };
}

function getItem(row: ListRow): ListItem {
  const item: ListItem = {};

  row.schema.forEach((itemField) => (item[itemField.name] = itemField.model));

  return item;
}

// Writes only when the error changes: this runs inside the deep rows watcher, and the error Set is part of the rows.
function showItemFieldError(itemField: FieldSchema) {
  const error: string | null = props.field?.settings?.getItemFieldError?.(itemField) ?? null;
  const currentError = [...itemField.state.errors][0] ?? null;
  if (error === currentError) return;

  itemField.state.errors.clear();

  if (error) itemField.state.errors.add(error);
}

// EVENTS
function onModelChange(items: ListItem[] | undefined) {
  // Our own emit coming back: the rows already hold it, and rebuilding them would remount every item form. It comes
  // back wrapped in the parent's reactive proxy, so compare the raw arrays; a plain `===` never matches and the field
  // rebuilds and emits forever.
  if (items && toRaw(items) === toRaw(lastEmittedItems)) return;

  rows.value = (Array.isArray(items) ? items : []).map(buildRow);
}

function onRowsChange() {
  rows.value.forEach((row) => row.schema.forEach(showItemFieldError));

  const items = rows.value.map(getItem);

  lastEmittedItems = items;
  model.value = items;
}

function onAddItem() {
  if (!canAddItem.value) return;

  rows.value.push(buildRow(props.field?.settings?.createItem?.() ?? {}));
}

function onRemoveItem(row: ListRow) {
  const index = rows.value.indexOf(row);
  if (index === -1) return;

  rows.value.splice(index, 1);
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <draggable
      v-model="rows"
      class="flex flex-col gap-3"
      handle=".list-item-handle"
      :disabled="isDisabled"
      :item-key="getRowKey"
    >
      <template #item="{ element: row, index }">
        <div class="rounded border border-slate-200 bg-slate-50 p-3">
          <div class="mb-2 flex items-center gap-2">
            <i class="list-item-handle pi pi-bars cursor-grab text-slate-400" />

            <span class="flex-1 text-sm font-semibold text-slate-700">{{ itemLabel }} {{ index + 1 }}</span>

            <Button
              text
              icon="pi pi-trash"
              severity="danger"
              size="small"
              type="button"
              :aria-label="$t('components.formFields.list.remove')"
              :disabled="isDisabled"
              @click="onRemoveItem(row)"
            />
          </div>

          <FormTemplate
            :granularValidation="false"
            :schema="row.schema"
          />
        </div>
      </template>
    </draggable>

    <div class="flex flex-wrap items-center gap-3">
      <Button
        outlined
        icon="pi pi-plus"
        severity="secondary"
        size="small"
        type="button"
        :disabled="!canAddItem"
        :label="$t('components.formFields.list.add', { item: itemLabel })"
        @click="onAddItem"
      />

      <span
        v-if="maxItems"
        class="text-xs text-slate-500"
      >
        {{ $t('components.formFields.list.max', { count: rows.length, max: maxItems }) }}
      </span>
    </div>
  </div>
</template>
